import { createContext, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { UseCheckoutResult } from './types';
import useCheckout from './useCheckout';
import { CheckoutStep } from './enums';

const CheckoutContext = createContext<UseCheckoutResult | undefined>(undefined);

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);

  if (!context) {
    throw new Error(
      'useCheckoutContext must be used within a CheckoutProvider',
    );
  }

  return context;
};

type Path = { checkoutId: string };

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const value = useCheckout();
  const { checkoutId } = router.query as Path;

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (
      !router.pathname.startsWith('/checkout') ||
      !checkoutId ||
      checkoutId === '' ||
      !value ||
      !value.checkout ||
      value.step !== CheckoutStep.Payment
    ) {
      return;
    }

    console.log('Verifying checkout ID from route', {
      currentCheckoutId: value.checkout.id,
      routeCheckoutId: checkoutId,
      route: router.pathname,
    });

    if (value.checkout?.id && value.checkout.id !== checkoutId) {
      console.log('Checkout ID mismatch, updating checkout ID from route', {
        currentCheckoutId: value.checkout.id,
        routeCheckoutId: checkoutId,
      });

      value.setLoading(true);

      try {
        value.updateCheckout(checkoutId, {
          shippingAddress: value.checkout.shippingAddress || undefined,
          billingAddress: value.checkout.billingAddress || undefined,
        });
      } catch (e) {
        console.log('Error updating checkout ID from route', {
          error: e,
        });
      } finally {
        value.setLoading(false);
      }
    }
  }, [router.isReady, router.pathname, checkoutId]);

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
